import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TotalDataDisplay from 'components/Table/TotalDataDisplay';
import SortDropdown, { SortFields } from 'components/Table/SortDropdown';
import TableFooter from 'components/Table/TableFooter';
import Table from 'components/Table';
import SearchInput from 'components/SearchInput';
import { useRouter } from 'next/router';
import { useGetEventAnalyticsGuests } from 'legacy/events/hooks/useGetEventAnalyticsGuests';
import { useDebounce } from 'use-debounce';
import { EventAnalyticsGuestsSortBy } from 'legacy/events/types';
import Loader from 'legacy/common/components/layout/Loader';
import GuestDetailsModal from 'legacy/events/components/guest-list/GuestDetailsModal';
import { StepsIds } from 'screens/events/EventDashboard/components/new/OptionsModal/types';
import OptionsModal from 'screens/events/EventDashboard/components/new/OptionsModal';

import { saveAs } from 'file-saver';
import { useExportEventGuests } from 'legacy/events/hooks';
import { MdMoreHoriz } from 'react-icons/md';
import dayjs from 'dayjs';
import DownloadCSVButton from '../DownloadCSVButton';

const NUMBER_OF_ITEMS_PER_PAGE = 10;

export const SECTION_ID = 'manage-guests-section';

const ManageGuestsSection = ({ isOrganizer }: { isOrganizer: boolean }) => {
  const { t } = useTranslation(['events']);
  const router = useRouter();
  const eventId = router.query.slug as string;

  const [sortOrder, setSortOrder] = useState<{
    sort: EventAnalyticsGuestsSortBy;
    order: 'asc' | 'desc';
    type: 'number' | 'text' | 'date';
  }>({
    sort: 'purchaseDate',
    order: 'asc',
    type: 'date'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [isGuestDetailsModalOpen, setIsGuestDetailsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 200);
  const [showTicketOptions, setShowTicketOptions] = useState<StepsIds | null>(
    null
  );
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketOptionsModal, setShowTicketOptionsModal] = useState(false);

  const {
    data: guests,
    isLoading,
    isFetching,
    hasPreviousPage,
    fetchPreviousPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchGuestList
  } = useGetEventAnalyticsGuests({
    eventId,
    numberOfItems: 10,
    sort: sortOrder?.sort,
    order: sortOrder?.order,
    query: debouncedSearch === '' ? undefined : debouncedSearch
  });
  const totalPages = guests?.pages;
  const rowsPerPage = NUMBER_OF_ITEMS_PER_PAGE;

  const handleSort = (sortDetails: {
    field: EventAnalyticsGuestsSortBy;
    order: 'asc' | 'desc';
    type: 'number' | 'text' | 'date';
  }) => {
    setSortOrder({
      sort: sortDetails.field,
      order: sortDetails.order,
      type: sortDetails.type
    });
  };

  const handleSearchChange = (event) => {
    setSearch(event);
  };

  const fields: SortFields[] = [
    {
      label: t('events:dashboard.guests.guestName'),
      value: 'guestName',
      type: 'text',
      field: 'guestName',
      order: 'asc'
    },
    {
      label: t('events:dashboard.guests.ticketTier'),
      value: 'ticketTierName',
      type: 'text',
      field: 'ticketTierName',
      order: 'asc'
    },
    {
      label: t('events:dashboard.guests.purchaseDate'),
      value: 'purchaseDate',
      type: 'date',
      field: 'purchaseDate',
      order: 'asc'
    },
    {
      label: t('events:dashboard.guests.scanned'),
      value: 'scanned',
      type: 'number',
      field: 'scanned',
      order: 'asc'
    },
    {
      label: t('events:dashboard.guests.lastScanned'),
      value: 'lastScanned',
      type: 'date',
      field: 'lastScanned',
      order: 'asc'
    },
    {
      label: t('events:dashboard.guests.discountCode'),
      value: 'discountCode',
      type: 'text',
      field: 'discountCode',
      order: 'asc'
    },
    {
      label: t('events:dashboard.guests.trackingLink'),
      value: 'trackingLink',
      type: 'text',
      field: 'trackingLink',
      order: 'asc'
    },
    {
      label: '',
      value: 'actions',
      type: 'text',
      field: 'actions',
      order: 'asc'
    }
  ];

  const onSelectTicket = (ticket) => {
    if (ticket?.id) {
      setSelectedTicket(ticket);
    }
    setIsGuestDetailsModalOpen(true);
  };

  const onShowMoreOptions = (ticket) => {
    setShowTicketOptions(StepsIds.DefaultOptions);
    if (ticket?.id) {
      setSelectedTicket(ticket);
    }
    setShowTicketOptionsModal(true);
  };

  const onCloseMoreOptions = () => {
    setShowTicketOptions(null);
    setShowTicketOptionsModal(false);
    setSelectedTicket(null);
  };

  const onGuestModalClose = () => {
    setSelectedTicket(null);
    setIsGuestDetailsModalOpen(false);
  };

  const onRefundTicket = (ticket) => {
    setShowTicketOptionsModal(true);
    setShowTicketOptions(StepsIds.SubmitRefundStep);
    if (ticket?.id) {
      setSelectedTicket(ticket);
    }
    setIsGuestDetailsModalOpen(false);
  };

  const formatDate = (dateValue?: Date): string => {
    const date = dayjs(dateValue);
    return date.isValid() ? date.format('YYYY/MM/DD HH:mm') : '-';
  };

  const { refetch: fetchExcelData, isFetching: isFetchingExportGuests } =
    useExportEventGuests(eventId, {
      isEnabled: false
    });
  const onClickExportGuests = async () => {
    const { data: rawData } = await fetchExcelData();
    const blob = new Blob([rawData], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
    });

    saveAs(blob, 'Bounce-Guests.xlsx');
  };

  useEffect(() => {
    // If the user wants to go to the manage guests section, scroll to it
    if (!isLoading && router.asPath.includes(`#${SECTION_ID}`)) {
      const element = document.getElementById(SECTION_ID);

      if (element) {
        window.scrollTo({
          top: element.offsetTop,
          behavior: 'smooth'
        });
      }
    }
  }, [router.asPath, isLoading]);

  if (isLoading) {
    return <Loader isLoading />;
  }

  return (
    <div>
      <div className="flex justify-between  mb-4 items-center">
        <h2 className="text-xl font-bold flex flex-row" id={SECTION_ID}>
          <img src="/img/events/people.svg" alt="guests" /> &nbsp;{' '}
          {t('events:dashboard.guests.manageGuests')}
        </h2>
        <div className="flex text-right">
          <DownloadCSVButton
            onClick={onClickExportGuests}
            loading={isFetchingExportGuests}
          />
        </div>
      </div>
      <div className="font-normal text-sm text-neutral-600 flex justify-between items-center">
        <TotalDataDisplay
          currentPage={guests?.items.length || 1}
          totalResults={guests?.totalResults || 100}
          label={t('events:dashboard.guests.guests')}
        />
        <div className="flex gap-2">
          <SearchInput
            onSearchChange={handleSearchChange}
            search={search}
            placeholder={t('events:dashboard.guests.searchByGuestName')}
          />
          <SortDropdown
            fields={[
              {
                label: 'Purchase Date: Newest',
                value: 'purchaseDateNewest',
                type: 'date',
                field: 'purchaseDate',
                order: 'desc'
              },
              {
                label: 'Purchase Date: Oldest',
                value: 'purchaseDateOldest',
                type: 'date',
                field: 'purchaseDate',
                order: 'asc'
              },
              {
                label: 'Guest Name: A-Z',
                value: 'guestNameAZ',
                type: 'text',
                field: 'guestName',
                order: 'asc'
              },
              {
                label: 'Guest Name: Z-A',
                value: 'guestNameZA',
                type: 'text',
                field: 'guestName',
                order: 'desc'
              },
              {
                label: 'Last Scanned: Newest',
                value: 'lastScannedNewest',
                type: 'date',
                field: 'lastScanned',
                order: 'desc'
              },
              {
                label: 'Last Scanned: Oldest',
                value: 'lastScannedOldest',
                type: 'date',
                field: 'lastScanned',
                order: 'asc'
              }
            ]}
            value={sortOrder.sort}
            order={sortOrder.order}
            type={sortOrder.type}
            handleSort={handleSort}
          />
        </div>
      </div>
      {guests?.items && (
        <div className="pt-4">
          <Table
            headers={fields.map((field) => field.label)}
            rows={guests?.items.map((item) => (
              <tr
                key={`event-guests-dashboard-row-${item.id}`}
                className="cursor-pointer"
              >
                <td
                  className="px-4 py-3 text-[#575757] text-xs"
                  onClick={() => onSelectTicket(item)}
                >
                  {item?.guestName}
                </td>
                <td
                  className="px-4 py-3 text-[#575757] text-xs"
                  onClick={() => onSelectTicket(item)}
                >
                  {item?.ticketTierName}
                </td>
                <td
                  className="px-4 py-3 text-[#575757] text-xs"
                  onClick={() => onSelectTicket(item)}
                >
                  {formatDate(item?.purchaseDate)}
                </td>
                <td
                  className="px-4 py-3 text-[#575757] text-xs"
                  onClick={() => onSelectTicket(item)}
                >
                  {item?.totalScans}
                </td>
                <td
                  className="px-4 py-3 text-[#575757] text-xs"
                  onClick={() => onSelectTicket(item)}
                >
                  {formatDate(item?.lastScanned)}
                </td>
                <td
                  className="px-4 py-3 text-[#575757] text-xs"
                  onClick={() => onSelectTicket(item)}
                >
                  {item?.discountCode}
                </td>
                <td
                  className="px-4 py-3 text-[#575757] text-xs"
                  onClick={() => onSelectTicket(item)}
                >
                  {item?.trackingLink}
                </td>
                <td className="px-4 py-3 text-[#575757] text-xs">
                  <button
                    aria-label="More options"
                    type="button"
                    onClick={() => onShowMoreOptions(item)}
                  >
                    <MdMoreHoriz size={24} />
                  </button>
                </td>
              </tr>
            ))}
          />
        </div>
      )}
      <TableFooter
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        totalResults={guests?.totalResults || 0}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        onPreviousPage={() => {
          fetchPreviousPage();
          setCurrentPage(currentPage - 1);
        }}
        onNextPage={() => {
          fetchNextPage();
          setCurrentPage(currentPage + 1);
        }}
        isFetching={isFetching}
      />

      {showTicketOptionsModal && selectedTicket && (
        <OptionsModal
          ticket={selectedTicket}
          onClose={onCloseMoreOptions}
          onViewDetails={() => setIsGuestDetailsModalOpen(true)}
          eventId={eventId}
          refetchGuestList={refetchGuestList}
          isOrganizer={isOrganizer}
          showTicketOptions={showTicketOptions}
        />
      )}
      {isGuestDetailsModalOpen && (
        <GuestDetailsModal
          eventId={eventId}
          selectedTicket={selectedTicket.id}
          onClose={onGuestModalClose}
          onRefundTicket={onRefundTicket}
          isOrganizer={isOrganizer}
          isCloseEnabled
        />
      )}
    </div>
  );
};

export default ManageGuestsSection;
